import { useStorage } from '@vueuse/core'
import { MEP_CONTACT_GROUP_ID } from '~/contants/mep'
interface User {
  id?: number
  email?: string
  api_token?: string
  tenant_identifier?: string
  api_tokenV2?: string
  tenant_identifierV2?: string
  mid?: number
}
export const useUserStore = defineStore('userStore', () => {
  const toast = useToast()

  const { apiBaseUrl, apiBaseUrlV2 } = useAppConfig()
  const user = ref(useStorage('user', {} as User, localStorage))
  const calendarCategories = ref()
  const hasSupervisor = ref<boolean | null>(null)

  const contact = ref(
    useStorage('contact', {} as Contact & { token?: string; tenantId?: string }, localStorage)
  )

  // The organisation a contact belongs to, used to show its logo next to the
  // tenant logo. Stored so a reload does not refetch it.
  const mainContact = ref(useStorage('mainContact', {} as Contact | Record<string, never>))

  const mainContactPicture = computed(() => mainContact.value?.profile_picture_url || '')

  const isFireSafetyGroup = computed(
    () => !!contact.value.contact_groups?.find((group) => group.id === 10)
  )

  const isMepPartner = computed(
    () => !!contact.value.contact_groups?.find((group) => group.id === MEP_CONTACT_GROUP_ID)
  )

  const isLoggedIn = computed(() => !!user.value.api_token)
  const isLoggedInAsContact = computed(() => !!contact.value.token)
  const userEmail = computed(() => user.value.email ?? '-')
  const userProfilePicture = computed(() =>
    user?.value?.id
      ? `https:\\/\\/my.1tool.com\\/crm_files\\/users\\/${user.value.id}\\/logo.jpg`
      : 'https:\\/\\/my.1tool.com\\/images\\/standard_picture.jpg'
  )

  // Contact login with email and password
  async function contactLoginWithEmail(email: string, password: string): Promise<boolean> {
    const { tenantId } = useAppConfig()

    const response = (await fetch(`${apiBaseUrl}auth/contact`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant': tenantId
      },
      body: JSON.stringify({
        email,
        password,
        device_name: 'web'
      }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => {
      toast.add({
        severity: 'error',
        summary: 'stores.user.error.serviceNotReachable.title',
        detail: 'stores.user.error.serviceNotReachable.description'
      })
    })) as any

    if (!response || !response.ok) {
      throw new Error(response?.status || '500')
    }

    const data = await response.json()
    if (data?.api_token) {
      contact.value.token = data.api_token
      contact.value.tenantId = tenantId
      await fetchContactProfile()
      return true
    }
    return false
  }

  // Request SMS code for phone login
  async function requestSmsCode(phone: string, message: string): Promise<boolean> {
    const { tenantId } = useAppConfig()

    const response = (await fetch(`${apiBaseUrl}auth/contact/number/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant': tenantId
      },
      body: JSON.stringify({ phone, message }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => {
      toast.add({
        severity: 'error',
        summary: 'stores.user.error.serviceNotReachable.title',
        detail: 'stores.user.error.serviceNotReachable.description'
      })
    })) as any

    return response && response.ok
  }

  // Contact login with phone and SMS code
  async function contactLoginWithPhone(phone: string, pin: string): Promise<boolean> {
    const { tenantId } = useAppConfig()

    const response = (await fetch(`${apiBaseUrl}auth/contact/number`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant': tenantId
      },
      body: JSON.stringify({
        phone,
        pin,
        device_name: 'web'
      }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => {
      toast.add({
        severity: 'error',
        summary: 'stores.user.error.serviceNotReachable.title',
        detail: 'stores.user.error.serviceNotReachable.description'
      })
    })) as any

    if (!response || !response.ok) {
      throw new Error(response?.status || '500')
    }

    const data = await response.json()
    if (data?.api_token) {
      contact.value.token = data.api_token
      contact.value.tenantId = tenantId
      await fetchContactProfile()
      return true
    }
    return false
  }

  // Request magic link for passwordless login
  async function requestMagicLink(email: string, link: string): Promise<boolean> {
    const { tenantId } = useAppConfig()

    const response = (await fetch(`${apiBaseUrl}auth/contact/magic-link/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant': tenantId
      },
      body: JSON.stringify({ email, link }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => {
      toast.add({
        severity: 'error',
        summary: 'stores.user.error.serviceNotReachable.title',
        detail: 'stores.user.error.serviceNotReachable.description'
      })
    })) as any

    return response && response.ok
  }

  // Posts to a contact auth endpoint of the tenant; null when the service is not reachable.
  async function postContactAuth(path: string, body: object): Promise<Response | null> {
    const { tenantId } = useAppConfig()
    return await fetch(`${apiBaseUrl}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Tenant': tenantId
      },
      body: JSON.stringify(body),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => {
      toast.add({
        severity: 'error',
        summary: 'stores.user.error.serviceNotReachable.title',
        detail: 'stores.user.error.serviceNotReachable.description'
      })
      return null
    })
  }

  // E-mails the contact a four-digit PIN to set a new password. The answer is the same
  // whether the address is known or not, so a trainer cannot find out others' addresses.
  async function requestPasswordPin(email: string): Promise<boolean> {
    const response = await postContactAuth('auth/contact/password/request', { email })
    return !!response?.ok
  }

  // Sets a new password with the e-mailed PIN. A refusal says whether the PIN was wrong or
  // which rule the password broke.
  async function resetPasswordWithPin(
    email: string,
    pin: string,
    password: string
  ): Promise<{ isSuccess: boolean; isInvalidPin: boolean; message: string | null }> {
    const response = await postContactAuth('auth/contact/password/reset', { email, pin, password })
    if (response?.ok) return { isSuccess: true, isInvalidPin: false, message: null }

    const body = response ? await response.json().catch(() => null) : null
    const passwordError: string | null = body?.errors?.password?.[0] || null
    return {
      isSuccess: false,
      isInvalidPin: response?.status === 422 && !body?.errors,
      message: passwordError
    }
  }

  // Fetch contact profile after login
  async function fetchContactProfile(): Promise<void> {
    const profile = await useApi('css/profile?include=contactGroups', 'GET')
    if (profile?.data) {
      contact.value = { ...contact.value, ...profile.data }
    }
    await fetchMainContact()
  }

  // The parent organisation of the logged-in contact. Failing to load it only
  // costs the extra logo, so it never blocks the login.
  async function fetchMainContact(): Promise<void> {
    const mainContactId = contact.value.maincontact
    if (!mainContactId) {
      mainContact.value = {}
      return
    }
    if (mainContact.value?.id === mainContactId) return

    try {
      const response = await useApi(
        `css/contacts/${mainContactId}`,
        'GET',
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        true
      )
      mainContact.value = response?.data ?? {}
    } catch {
      mainContact.value = {}
    }
  }

  // Contact logout
  function contactLogout() {
    contact.value = {} as Contact & { token?: string; tenantId?: string }
    mainContact.value = {}
    navigateTo('/login')
  }

  // Legacy user login (kept for reference)
  async function login(email: string, password: string): Promise<boolean> {
    const responseV2 = (await fetch(`${apiBaseUrlV2}auth/user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        password,
        device_name: 'mac'
      }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => {
      toast.add({
        severity: 'error',
        summary: 'stores.user.error.serviceNotReachable.title',
        detail: 'stores.user.error.serviceNotReachable.description'
      })
    })) as any

    if (!responseV2.ok) {
      throw new Error(responseV2.status)
    }

    const response = (await fetch(`${apiBaseUrl}auth/user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        email,
        password,
        device_name: 'mac'
      }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => {
      toast.add({
        severity: 'error',
        summary: 'stores.user.error.serviceNotReachable.title',
        detail: 'stores.user.error.serviceNotReachable.description'
      })
    })) as any

    if (!response.ok) {
      throw new Error(response.status)
    }

    if (responseV2 && responseV2.status === 200 && response && response.status === 200) {
      const responseText = await response.text()
      const responseTextV2 = await responseV2.text()

      if (responseText && responseTextV2) {
        const { api_token: apiTokenV2, tenant_identifier: tenantIdentifierV2 } =
          JSON.parse(responseTextV2)
        const {
          api_token: apiToken,
          tenant_identifier: tenantIdentifier,
          id
        } = JSON.parse(responseText)

        user.value = {
          id,
          email,
          api_token: apiToken,
          tenant_identifier: tenantIdentifier,
          api_tokenV2: apiTokenV2,
          tenant_identifierV2: tenantIdentifierV2
        }

        if (!calendarCategories.value) await fetchCalendarCategories()

        return true
      } else {
        return false
      }
    } else {
      return false
    }
  }

  function logout() {
    user.value = {}
    contact.value = {} as Contact & { token?: string; tenantId?: string }
    mainContact.value = {}
    navigateTo('/login')
  }

  async function fetchCalendarCategories() {
    if (!user.value.id) return
    try {
      const response = await useApi(`calendar-categories?filter[user_id]=${user.value.id}`, 'GET')
      calendarCategories.value = response?.data ?? []
    } catch {}
  }

  return {
    user,
    userEmail,
    userProfilePicture,
    isLoggedIn,
    calendarCategories,
    hasSupervisor,
    contact,
    mainContact,
    mainContactPicture,
    isLoggedInAsContact,
    isFireSafetyGroup,
    isMepPartner,
    login,
    logout,
    contactLoginWithEmail,
    requestSmsCode,
    contactLoginWithPhone,
    requestMagicLink,
    requestPasswordPin,
    resetPasswordWithPin,
    fetchContactProfile,
    contactLogout
  }
})
// Use HMR for this store
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
